'use client'

import React, { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { cn } from '@segway/core'

interface MagnifierContextType {
  x: number
  y: number
  showMagnifier: boolean
  setShowMagnifier: React.Dispatch<React.SetStateAction<boolean>>
  updateCoordinates: (x: number, y: number) => void
  smallImageWidth: number
  smallImageHeight: number
  setSmallImageSize: (width: number, height: number) => void
  zoomLevel: number
  magnifiedScale: number
  setMagnifiedScale: React.Dispatch<React.SetStateAction<number>>
}

interface MagnifierProviderProps {
  children: ReactNode
  zoomLevel?: number
  magnifiedScale?: number
}

interface MagnifierImageProps {
  smallImage: string
  alt?: string
  width?: number | string
  height?: number | string
}

interface MagnifiedAreaProps {
  largeImage: string
  alt?: string
  magnifiedScale?: number
}

const MagnifierContext = createContext<MagnifierContextType | undefined>(undefined)

export const MagnifierProvider = ({
  children,
  zoomLevel = 2,
  magnifiedScale: initialMagnifiedScale = 1,
}: MagnifierProviderProps) => {
  const [x, setX] = useState(0)
  const [y, setY] = useState(0)
  const [showMagnifier, setShowMagnifier] = useState(false)
  const [smallImageWidth, setSmallImageWidth] = useState(0)
  const [smallImageHeight, setSmallImageHeight] = useState(0)
  const [magnifiedScale, setMagnifiedScale] = useState(initialMagnifiedScale)

  const updateCoordinates = (newX: number, newY: number) => {
    setX(newX)
    setY(newY)
  }

  const setSmallImageSize = (width: number, height: number) => {
    setSmallImageWidth(width)
    setSmallImageHeight(height)
  }

  return (
    <MagnifierContext.Provider
      value={{
        x,
        y,
        showMagnifier,
        setShowMagnifier,
        updateCoordinates,
        smallImageWidth,
        smallImageHeight,
        setSmallImageSize,
        zoomLevel,
        magnifiedScale,
        setMagnifiedScale,
      }}>
      {children}
    </MagnifierContext.Provider>
  )
}

const useMagnifierContext = () => {
  const context = useContext(MagnifierContext)
  if (!context) {
    throw new Error('useMagnifierContext 必须在 MagnifierProvider 内使用')
  }
  return context
}

export const MagnifierImage = ({
  smallImage,
  width = '100%',
  height = '100%',
}: MagnifierImageProps) => {
  const {
    x,
    y,
    showMagnifier,
    setShowMagnifier,
    updateCoordinates,
    setSmallImageSize,
    smallImageWidth,
    smallImageHeight,
    zoomLevel,
    magnifiedScale,
  } = useMagnifierContext()
  const imgRef = useRef<HTMLImageElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!imgRef.current) return
    const { left, top } = imgRef.current.getBoundingClientRect()
    const newX = e.clientX - left
    const newY = e.clientY - top
    updateCoordinates(newX, newY)
  }

  const handleImageLoad = () => {
    if (imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect()
      setSmallImageSize(rect.width, rect.height)
    }
  }

  const indicatorSize = {
    width: (smallImageWidth * magnifiedScale) / zoomLevel,
    height: (smallImageHeight * magnifiedScale) / zoomLevel,
  }

  return (
    <div className="relative inline-block overflow-visible" style={{ width, height }}>
      <div
        ref={imgRef}
        className="h-full w-full"
        onMouseEnter={() => setShowMagnifier(true)}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setShowMagnifier(false)}>
        <Image
          onLoad={handleImageLoad}
          alt="image"
          src={smallImage}
          fill
          className="cursor-zoom-in rounded-lg object-cover"
        />
      </div>

      {showMagnifier && smallImageWidth > 0 && (
        <div
          className="pointer-events-none absolute border-2 border-gray-3"
          style={{
            left: `${x - indicatorSize.width / 2}px`,
            top: `${y - indicatorSize.height / 2}px`,
            width: `${indicatorSize.width}px`,
            height: `${indicatorSize.height}px`,
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
            zIndex: 10,
          }}
        />
      )}
    </div>
  )
}

export const MagnifiedArea = ({
  largeImage,
  magnifiedScale: propMagnifiedScale = 1,
}: MagnifiedAreaProps) => {
  const {
    x,
    y,
    showMagnifier,
    smallImageWidth,
    smallImageHeight,
    zoomLevel,
    setMagnifiedScale,
    magnifiedScale,
  } = useMagnifierContext()

  useEffect(() => {
    setMagnifiedScale(propMagnifiedScale)
  }, [propMagnifiedScale, setMagnifiedScale])

  // 根据 magnifiedScale 计算放大区域尺寸
  const finalWidth = smallImageWidth * magnifiedScale
  const finalHeight = smallImageHeight * magnifiedScale

  // 计算大图的位置，使得小图对应的内容在放大区域正中
  const largeImageLeft = finalWidth / 2 - x * zoomLevel
  const largeImageTop = finalHeight / 2 - y * zoomLevel

  if (!smallImageWidth || !smallImageHeight) return null

  return (
    <div
      className={cn(
        'absolute overflow-hidden rounded-lg border border-gray-3 bg-white shadow-xl',
        showMagnifier ? 'block' : 'hidden',
      )}
      style={{
        left: 'calc(100% + 20px)',
        top: '0',
        width: finalWidth,
        height: finalHeight,
        zIndex: 20,
      }}>
      <Image
        src={largeImage}
        alt="zoomed image"
        width={smallImageWidth * zoomLevel}
        height={smallImageHeight * zoomLevel}
        className="absolute max-w-none rounded-lg object-cover"
        style={{
          width: smallImageWidth * zoomLevel,
          height: smallImageHeight * zoomLevel,
          left: largeImageLeft,
          top: largeImageTop,
        }}
      />
    </div>
  )
}
